import { DelegateOf, RepresentationMode, User } from "@/shared/types/user";
import axios from "axios";
import { createContext, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RepresentedEntityForm } from "@/forms";
import { Loader, Modal } from "@/shared/components";
import { useQueryClient } from "@tanstack/react-query";

type ProviderProps = {
  user: User | null | undefined;
  representedEntity: DelegateOf | null | undefined;
  representationMode: RepresentationMode | null;
  isUpdatingDelegation: boolean;

  setDelegation(
    delegateOf: DelegateOf | null | undefined,
    mode: RepresentationMode
  ): Promise<void>;

  showRepresentedEntityModal(): void;

  login(): void;

  logout(isLocalLogout: boolean): void;

  refresh(): void;
};

const AuthContext = createContext<ProviderProps>({
  user: null,
  representedEntity: null,
  representationMode: null,
  isUpdatingDelegation: false,
  setDelegation: async () => {},
  showRepresentedEntityModal: () => {},
  login: () => {},
  logout: () => {},
  refresh: () => {},
});

const API_BASE_URL =
  import.meta.env.MODE === "development" ? "http://localhost:8081" : "";

const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>();
  const [representedEntity, setRepresentedEntity] =
    useState<DelegateOf | null>();
  const [representationMode, setRepresentationMode] =
    useState<RepresentationMode | null>(
      () => localStorage.getItem("representationMode") as RepresentationMode
    );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalClosable, setIsModalClosable] = useState(false);
  const [isUpdatingDelegation, setIsUpdatingDelegation] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const hideModal = useCallback(() => {
    setIsModalOpen(false);
    setIsModalClosable(false);
  }, []);

  const setDelegation = useCallback(
    async (
      delegateOf: DelegateOf | null | undefined,
      mode: RepresentationMode
    ) => {
      setIsUpdatingDelegation(true);
      setRepresentedEntity(null);
      setRepresentationMode(mode);
      localStorage.setItem("representationMode", mode);
      hideModal();

      try {
        await axios.post(`${API_BASE_URL}/api/user/delegation`, null, {
          params: {
            id: delegateOf?.id,
            mode: mode,
          },
          withCredentials: true,
        });

        await queryClient.resetQueries();

        const representedEntity = await getDelegation();
        setRepresentedEntity(representedEntity);

        if (delegateOf?.monitoredAreas?.length === 0) {
          navigate("/");
        }
      } catch (error) {
        if (axios.isAxiosError(error)) {
          console.error("Failed to set represented person:", error.message);
        }
        setRepresentedEntity(null);
      } finally {
        setIsUpdatingDelegation(false);
      }
    },
    [navigate, hideModal, queryClient]
  );

  const getDelegation = async () => {
    const representedEntityResponse = await axios.get<DelegateOf>(
      `${API_BASE_URL}/api/user/delegation`,
      {
        withCredentials: true,
      }
    );
    return representedEntityResponse.data;
  };

  const hasSession = async () => {
    const representedEntityResponse = await axios.get<boolean>(
      `${API_BASE_URL}/api/user`,
      {
        withCredentials: true,
      }
    );
    return representedEntityResponse.data;
  };

  const showRepresentedEntityModal = useCallback(() => {
    if (!user?.delegateOf) return;
    setIsModalClosable(true);
    setIsModalOpen(true);
  }, [user]);

  const getUser = useCallback(async () => {
    try {
      const response = await axios.get<User>(
        `${API_BASE_URL}/api/user/profile`,
        {
          withCredentials: true,
        }
      );

      const profile = response.data;
      const representedEntity = await getDelegation();

      setUser(profile);

      if (!representedEntity) {
        if (profile.delegateOf.length === 1) {
          await setDelegation(profile.delegateOf[0], "DIRECT");
        } else {
          setIsModalClosable(false);
          setIsModalOpen(true);
          setRepresentedEntity(null);
        }
      } else {
        setRepresentedEntity(representedEntity);
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error("Failed to fetch user profile:", error.message);
      }
      setUser(null);
      setRepresentedEntity(null);
    }
  }, [setDelegation]);

  useEffect(() => {
    hasSession().then(hasSession => {
      if (hasSession) {
        getUser();
      } else {
        setUser(null);
        setRepresentedEntity(null);
      }
    });
  }, [getUser]);

  const login = async () => {
    try {
      await axios.get(`${API_BASE_URL}/login/cas`, {
        withCredentials: true,
      });
      await getUser();
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error("Login failed:", error.message);
      }
      setUser(null);
      setRepresentedEntity(null);
    }
  };

  const logout = async (isLocalLogout: boolean) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/session`, {
        withCredentials: true,
      });

      const res = await axios.get(`${API_BASE_URL}/logout/cas`, {
        withCredentials: true,
      });

      if (isLocalLogout) {
        navigate("/");
      } else {
        window.location.href = res.data.url;
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error("Logout failed:", error.message);
      }
      navigate("/");
    } finally {
      setUser(null);
      setRepresentedEntity(null);
      setRepresentationMode(null);
      localStorage.removeItem("representationMode");
    }
  };

  const refresh = async () => {
    try {
      await getUser();
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error("Refresh failed:", error.message);
      }
      setUser(null);
      setRepresentedEntity(null);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        representedEntity,
        representationMode,
        isUpdatingDelegation,
        setDelegation,
        showRepresentedEntityModal,
        logout,
        login,
        refresh,
      }}
    >
      {isUpdatingDelegation && (
        <div className="fixed inset-0 bg-white/80 z-9999 flex items-center justify-center">
          <Loader />
        </div>
      )}
      {children}
      {isModalOpen && user?.delegateOf && (
        <Modal
          show={isModalOpen}
          onHide={hideModal}
          size="5xl"
          closable={isModalClosable}
        >
          <RepresentedEntityForm
            delegateList={user.delegateOf}
            currentDelegate={representedEntity}
            setDelegation={setDelegation}
            onSelect={() => {
              navigate("/");
            }}
          />
        </Modal>
      )}
    </AuthContext.Provider>
  );
};

export default AuthProvider;

export { AuthContext };
